import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import useWidth from '../../../../../components/Hooks/useWidth';
import useHandleAppException from '../../../../../hooks/useHandleAppException';
import LangEnum from '../../../../../lang/LangEnum';
import {
  useGetOrderById,
  useGetOrderPaymentData,
} from '../../../../../services/modules/order/order.queries';
import usePaymentReleaseSettings from '../../../../../stores/GeneralSettings/hooks/usePaymentReleaseSettings';
import snackbarStore from '../../../../../stores/Snackbar.store';
import { useApplicationContext } from '../../../../context/ApplicationContext/ApplicationContext';
import { PaymentValueType } from '../../../../payment-method/payment-method.types';
import { useTranslation } from '../../../../translation/TranslationContext';
import { useAbility } from '../../../../users/AbilityContext';
import { UserRole } from '../../../../users/roles';
import { getLastOrderGatewayPayment } from '../../../actions/utils/orderSummaryAction.utils';
import { OrderStatus } from '../../../interfaces/order-status.interface';
import { OrderType } from '../../../order.types';
import { OrderGatewayPaymentStatus } from '../../../payment/enums/orderPaymentGatewayStatus.enum';
import { OrderPaymentActionsUtils } from '../../../payment/orderPaymentActions.utils';
import { ordersRepository } from '../../../repositories/ordersRepository/orders.repository';
import { OrderPaymentPartialValueDataDTO } from '../../../repositories/ordersRepository/types/orderPaymentRepository.types';
import { HandleDefaultStateParams } from '../../types/orderPaymentModal.types';
import { OrderModalPaymentType } from '../enums/OrderModalPaymentType.enum';
import { OrderPaymentModalView } from '../enums/OrderModalView.enum';

interface UseOrderPaymentModalProps {
  isOpen: boolean;
  orderId: string;
  onClose: () => void;
  defaultView?: OrderPaymentModalView;
}

export const useOrderPaymentModal = ({
  isOpen,
  onClose,
  orderId,
  defaultView,
}: UseOrderPaymentModalProps) => {
  const { mobileWidth } = useWidth();
  const { team, erpIntegration } = useApplicationContext();
  const { translate } = useTranslation();
  const { handleErrorWithFallback } = useHandleAppException();
  const queryClient = useQueryClient();
  const ability = useAbility();

  const { requirePaymentReleaseForImmediate, requirePaymentReleaseForPreOrder, refundLimitHours } =
    usePaymentReleaseSettings();

  const [value, setValue] = useState({
    inputValue: 0,
    monetaryValue: 0,
  });
  const [view, setView] = useState<{ current: OrderPaymentModalView; paymentUrl?: string }>({
    current: defaultView || OrderPaymentModalView.PAYMENT,
    paymentUrl: undefined,
  });
  const [paymentType, setPaymentType] = useState(OrderModalPaymentType.VALUE);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    data: order,
    isLoading,
    refetch,
  } = useGetOrderPaymentData({
    isOpen,
    orderId,
  });

  const activePaymentUrl = useMemo(
    () =>
      order?.orderGatewayPayments.find((payment) => {
        const statuses = [
          OrderGatewayPaymentStatus.PAID,
          OrderGatewayPaymentStatus.PENDING,
          OrderGatewayPaymentStatus.PAYMENT_FAILED,
        ];

        return statuses.includes(payment.status);
      }),
    [order?.orderGatewayPayments],
  );

  const handleDefaultState = ({
    orderTotalValue,
    paymentType,
    paymentValueType,
    minimumPaymentPercentage,
  }: HandleDefaultStateParams) => {
    if (activePaymentUrl) {
      setPaymentType(OrderModalPaymentType.VALUE);
      setValue({
        inputValue: activePaymentUrl.value,
        monetaryValue: activePaymentUrl.value,
      });
      return;
    }

    if (paymentValueType === PaymentValueType.FULL_VALUE) {
      setPaymentType(OrderModalPaymentType.VALUE);
      setValue({
        inputValue: orderTotalValue,
        monetaryValue: orderTotalValue,
      });
      return;
    }

    if (paymentValueType === PaymentValueType.PARTIAL_VALUE) {
      const percentageValue = minimumPaymentPercentage || 0;
      const monetaryValue = +((percentageValue * orderTotalValue) / 100).toFixed(2);

      setValue({
        inputValue: paymentType === OrderModalPaymentType.VALUE ? monetaryValue : percentageValue,
        monetaryValue,
      });
    }
  };

  const { queryKey: orderQueryKey } = useGetOrderById({ enabled: false, id: orderId });

  const onCreatePaymentLink = async (onSuccess: (paymentUrl: string) => void) => {
    if (activePaymentUrl) {
      return onSuccess(activePaymentUrl.paymentUrl);
    }

    setIsSubmitting(true);
    const response = await ordersRepository(orderId).createOrderPaymentLink(value.monetaryValue);
    setIsSubmitting(false);

    if (response.ok && response.data) {
      onSuccess(response.data.paymentUrl);
      await queryClient.refetchQueries({
        queryKey: orderQueryKey,
        type: 'active',
        exact: true,
      });
      snackbarStore.handleMessage({
        message: translate(LangEnum.ORDER_PAYMENT_LINK_CREATED_SUCCESSFULLY),
        type: 'success',
      });
      refetch();
      return;
    }

    snackbarStore.handleMessage({
      message: handleErrorWithFallback(
        response.data,
        translate(LangEnum.SNACKBAR_ERROR_OCCURRED_TRY_AGAIN),
      ),
      type: 'error',
    });
  };

  const onCancelPaymentLink = async () => {
    setIsSubmitting(true);
    const response = await ordersRepository(orderId).cancelOrderPaymentLink();
    setIsSubmitting(false);

    if (response.ok) {
      await queryClient.refetchQueries({
        queryKey: orderQueryKey,
        type: 'active',
        exact: true,
      });
      snackbarStore.handleMessage({
        message: translate(LangEnum.PAYMENT_LINK_CANCELED_SUCCESS),
        type: 'success',
      });
      onClose();
      return;
    }

    snackbarStore.handleMessage({
      message: handleErrorWithFallback(
        response.data,
        translate(LangEnum.PAYMENT_LINK_CANCEL_ERROR),
      ),
      type: 'error',
    });
  };

  const minimumPaymentPercentage =
    order?.paymentValueType === PaymentValueType.PARTIAL_VALUE ? order.minimumPaymentPercentage : 0;

  const orderTotalValue = order?.orderTotalValue || 0;
  const minimumValue = +((orderTotalValue * minimumPaymentPercentage) / 100).toFixed(2);

  const error = value.monetaryValue < minimumValue;

  const isInputDisabled =
    order?.paymentValueType === PaymentValueType.FULL_VALUE || !!activePaymentUrl;

  const lastGatewayPayment = getLastOrderGatewayPayment(order?.orderGatewayPayments);

  const mustShowCancelPaymentButton = order
    ? new OrderPaymentActionsUtils({
        order: {
          paymentStatus: order.paymentStatus,
          splitted: order.splitted || false,
          status: order.status as OrderStatus,
          type: order.type as OrderType,
          synchronized: order.synchronized,
        },
        ability,
        requirePaymentGateway: !!order.requirePaymentGateway,
        requirePaymentReleaseForImmediate,
        requirePaymentReleaseForPreOrder,
        role: team?.role as UserRole,
        lastGatewayPaymentStatus: lastGatewayPayment?.status,
        lastGatewayPaymentUpdatedAt: lastGatewayPayment?.updatedAt,
        refundLimitHours,
        integrationTrigger: erpIntegration?.integrationTrigger,
      }).showCancelPaymentButton() && !mobileWidth
    : false;

  const handleGeneratePaymentLink = () => {
    void onCreatePaymentLink((paymentUrl) => {
      window.open(paymentUrl, '_blank');
      onClose();
    });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (order) {
      handleDefaultState({
        minimumPaymentPercentage:
          (order as OrderPaymentPartialValueDataDTO)?.minimumPaymentPercentage || 0,
        orderTotalValue: order.orderTotalValue,
        paymentType,
        paymentValueType: order.paymentValueType,
      });
    }
  }, [order]);

  useEffect(() => {
    setView({ current: defaultView || OrderPaymentModalView.PAYMENT });
  }, [defaultView]);

  return {
    error,
    isInputDisabled,
    order,
    isLoading,
    minimumPaymentPercentage,
    orderTotalValue,
    value,
    paymentType,
    minimumValue,
    isSubmitting,
    view,
    hasActivePaymentLink: !!activePaymentUrl,
    mustShowCancelPaymentButton,
    onChangeView: setView,
    onChangePaymentType: setPaymentType,
    onChangeValue: setValue,
    handleDefaultState,
    handleGeneratePaymentLink,
    handleSharePaymentLink: () =>
      onCreatePaymentLink((paymentUrl) =>
        setView({ current: OrderPaymentModalView.SHARE_LINK, paymentUrl }),
      ),
    handleCancelPaymentLink: onCancelPaymentLink,
  };
};
